import React, { useRef, useEffect, useState, useCallback } from 'react';
import ChatMessage from './ChatMessage';
// import TypingIndicator from './TypingIndicator';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ChatInterface = ({
  chatMessages,
  currentMessage,
  setCurrentMessage,
  handleSubmit,
  isWaitingForResponse,
  isStreaming,
  streamingResponse,
  documents,
  handleFiles,
  removeDocument,
  analyzeDocuments,
  isAnalyzed
}) => {
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [chatMessages.length]);

  const handleScroll = useCallback(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    setShowScrollButton(el.scrollHeight - el.scrollTop - el.clientHeight > 40);
  }, []);

  useEffect(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToBottom = () => {
    chatContainerRef.current?.scrollTo({ top: chatContainerRef.current.scrollHeight, behavior: 'smooth' });
  };

  const copyAIResponses = () => {
    const aiText = chatMessages
      .filter(msg => msg.sender === 'ai')
      .map(msg => msg.text)
      .join('\n\n---\n\n');
    navigator.clipboard.writeText(aiText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  

  // Auto-resize textarea as content grows
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [currentMessage]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (currentMessage.trim() !== '' && (!isWaitingForResponse || isStreaming)) {
        handleSubmit(e);
      }
    }
  };
  
  // Render the Analyze button when no analysis has been performed yet
  const renderAnalyzeButton = () => {
    return (
      <div className="analyze-container">
        <button 
          className="analyze-button" 
          onClick={analyzeDocuments}
          disabled={documents.length === 0 || isWaitingForResponse}
        >
          Analyze Documents
        </button>
        <p className="analyze-hint">
          {documents.length === 0 
            ? "Upload documents first to enable analysis" 
            : "Click to analyze your uploaded documents"}
        </p>
      </div>
    );
  };
  
  // Render the chat interface once analysis has started
  const renderChatInterface = () => {
    return (
      <>
        <div className="chat-body">
        <div className="chat-container" ref={chatContainerRef}>
          {/* Display existing chat messages */}
          {chatMessages.map(msg => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          
          {(isWaitingForResponse || isStreaming) && !streamingResponse.trim() && (
            <div className="chat-spinner-overlay">
              <div className="chat-spinner">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}

          {/* Show streaming response with markdown */}
          {isStreaming && streamingResponse.trim() && (
            <div className="chat-message ai">
              <div className="message-bubble streaming">
                <div className="markdown-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {streamingResponse}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          )}

          {/* Copy button — only when idle and AI has responded */}
          {!isStreaming && !isWaitingForResponse && chatMessages.some(msg => msg.sender === 'ai') && (
            <div className="chat-actions">
              <button className="copy-ai-response" onClick={copyAIResponses} aria-label="Copy AI response">
                {copied ? (
                  <>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                      <path d="M2 7L5 10L11 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                      <rect x="4" y="4" width="7" height="8" rx="1" stroke="currentColor" strokeWidth="1.4"/>
                      <path d="M2 9V2h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Copy
                  </>
                )}
              </button>
            </div>
          )}
        </div>
        
        {showScrollButton && (
          <button className="scroll-to-bottom" onClick={scrollToBottom} aria-label="Scroll to bottom">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M2 5L7 10L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        </div>

        <div className="chat-input">
          <div className="chat-input-inner">
            <div className="chat-attachments">
              <button
                className="chat-add-file"
                onClick={() => fileInputRef.current.click()}
                aria-label="Add document"
              >
                <span className="chat-add-file-icon">+</span>
                <span className="chat-add-file-label">Add Document</span>
              </button>
              <input
                aria-label="Upload documents"
                tabIndex="-1"
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                multiple
                accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                onChange={(e) => { handleFiles(e.target.files); e.target.value = null; }}
              />
              {documents.map(doc => (
                <div key={doc.id} className="attachment-card">
                  <span className="attachment-icon">📄</span>
                  <span className="attachment-name">{doc.name}</span>
                  <button
                    className="attachment-remove"
                    onClick={() => removeDocument(doc.id)}
                    aria-label={`Remove ${doc.name}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="chat-input-row">
              <label htmlFor="chat-message-input" className="chat-input-label">
                Message
              </label>
              <textarea
                id="chat-message-input"
                ref={textareaRef}
                rows={1}
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isWaitingForResponse ? "Type your next message…" : "Ask a follow-up question… (Enter to send, Shift+Enter for newline)"}
              />
              <button
                onClick={handleSubmit}
                disabled={currentMessage.trim() === '' || (isWaitingForResponse && !isStreaming)}
                aria-label="Send message"
                title="Send chat"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="19" x2="12" y2="5" />
                  <polyline points="5 12 12 5 19 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };
  
  return (
    <div className="chat-section">
      {!isAnalyzed && chatMessages.length === 0 
        ? renderAnalyzeButton() 
        : renderChatInterface()}
    </div>
  );
};

export default ChatInterface;